/**
 * Development-only switches for forcing each payment-form failure state, so they can be looked at
 * without having to actually break a network or a backend.
 *
 * Paste ONE of these into the browser console at any point before reaching the payment step:
 *
 *   sessionStorage.setItem('simulatePaymentFailure', 'blocked')   provider SDK cannot be loaded
 *   sessionStorage.setItem('simulatePaymentFailure', 'config')    payment settings fetch fails
 *   sessionStorage.setItem('simulatePaymentFailure', 'amount')    amount-payable fetch fails
 *   sessionStorage.setItem('simulatePaymentFailure', 'zero')      amount payable comes back as $0
 *   sessionStorage.setItem('simulatePaymentFailure', 'stalled')   nothing fails, nothing arrives
 *   sessionStorage.setItem('simulatePaymentFailure', 'element')   SDK loads, card element will not mount
 *
 * then continue to the payment step as normal. `sessionStorage.removeItem('simulatePaymentFailure')`
 * clears it, and it is cleared automatically once served.
 *
 * Session storage rather than a query parameter on purpose: editing the address bar reloads the
 * page, and the booking wizard sends a fresh load back to the earliest incomplete step, so a
 * `?param` could never survive long enough to reach the payment form.
 *
 * Each one is ONE-SHOT: the first attempt fails and `Try again` then succeeds, so the retry path is
 * visible too rather than looking broken. Set the key again to re-arm.
 *
 * Inert unless `NODE_ENV === 'development'`, so a production build cannot be put into these states.
 */

export type SimulatedPaymentFailure = 'blocked' | 'config' | 'amount' | 'zero' | 'stalled' | 'element';

const STORAGE_KEY = 'simulatePaymentFailure';

const VALID: SimulatedPaymentFailure[] = ['blocked', 'config', 'amount', 'zero', 'stalled', 'element'];

/**
 * The answer already given for each kind this attempt.
 *
 * React StrictMode mounts every component twice in development, so an effect that consumed the
 * armed flag on its first pass found it gone on the second and carried on as though nothing were
 * simulated — the failure page appeared and was immediately replaced. Deciding once per attempt and
 * remembering the decision makes the answer stable however many times React re-runs the caller.
 */
const decisions = new Map<SimulatedPaymentFailure, boolean>();

const isDevelopment = () => process.env.NODE_ENV === 'development';

/** The failure currently armed, if any. */
export function requestedPaymentFailure(): SimulatedPaymentFailure | null {
	if (!isDevelopment() || typeof window === 'undefined') {
		return null;
	}

	try {
		const value = window.sessionStorage.getItem(STORAGE_KEY);
		return VALID.includes(value as SimulatedPaymentFailure) ? (value as SimulatedPaymentFailure) : null;
	} catch {
		// Private browsing and blocked storage both throw on access.
		return null;
	}
}

function disarm(): void {
	try {
		window.sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing to do — if storage cannot be written it could not have been armed either.
	}
}

/**
 * True when this failure should be simulated right now. The decision is cached until
 * {@link clearSimulationDecisions} is called, so a re-render or a StrictMode double-mount gets the
 * same answer rather than racing the one-shot.
 */
export function shouldSimulatePaymentFailure(kind: SimulatedPaymentFailure): boolean {
	const alreadyDecided = decisions.get(kind);
	if (alreadyDecided !== undefined) {
		return alreadyDecided;
	}

	const armed = requestedPaymentFailure() === kind;
	decisions.set(kind, armed);

	if (armed) {
		// Disarm the storage key immediately so a page reload is not stuck in the failure state;
		// the cached decision above is what keeps it stable for this attempt.
		disarm();
	}

	return armed;
}

/**
 * Forgets this attempt's decisions so the next one re-evaluates. Since the storage key was disarmed
 * when the failure was served, that next evaluation returns false and the form recovers — which is
 * what makes `Try again` visibly work.
 */
export function clearSimulationDecisions(): void {
	decisions.clear();
}

/**
 * Whether a `stalled` simulation is in force. Not a one-shot read: the stall is a timer rather than
 * a single decision, so the wrapper asks on every render and it stays true until the timer fires.
 */
export function isStallSimulationArmed(): boolean {
	return shouldSimulatePaymentFailure('stalled');
}

/** Marks the stall as served, so `Try again` after it loads normally. */
export function consumeStallSimulation(): void {
	decisions.set('stalled', false);
	disarm();
}
